import { useEffect, useState } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ShoppingCart, Package, Users, DollarSign, TrendingUp,
  Clock, CheckCircle, XCircle, ExternalLink, AlertTriangle, Gift, Wallet, Coins, Zap
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingPunchout, setStartingPunchout] = useState(false);

  const token = localStorage.getItem('enterprise_token');
  const user = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  // Check for errors from punchout return
  useEffect(() => {
    const error = searchParams.get('error');
    const reason = searchParams.get('reason');
    const debugId = searchParams.get('debug');

    if (error) {
      let message = "Erè nan koneksyon Amazon";

      switch (error) {
        case 'punchout_failed':
          message = `Echèk nan resevwa panye Amazon. ${reason ? decodeURIComponent(reason) : ''}`;
          break;
        case 'session_not_found':
          message = "Sesyon Amazon pa jwenn. Tanpri eseye ankò.";
          break;
        case 'no_cart_data':
          message = "Amazon pa voye done panye. Sa ka rive nan mòd tès.";
          break;
      }

      toast.error(message, { duration: 8000 });

      if (debugId) {
        console.log("Debug ID for support:", debugId);
      }

      // Clear the error from URL
      navigate('/enterprise/dashboard', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/enterprise/dashboard/stats`, { headers }),
        axios.get(`${API}/enterprise/orders/pending-approval`, { headers })
      ]);

      setStats(statsRes.data);
      setPendingOrders(pendingRes.data.orders || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('enterprise_token');
        navigate('/enterprise');
      }
    } finally {
      setLoading(false);
    }
  };

  const startPunchoutSession = async () => {
    setStartingPunchout(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/punchout/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.redirect_url) {
        toast.success("Ap ouvri Amazon Business...");
        // Store session info
        localStorage.setItem('punchout_session', JSON.stringify({
          session_id: response.data.session_id,
          buyer_cookie: response.data.buyer_cookie
        }));
        // Redirect to Amazon
        window.open(response.data.redirect_url, '_blank');
      } else {
        toast.error("Pa kapab kòmanse sesyon Punchout");
      }
    } catch (error) {
      console.error("Punchout error:", error);
      toast.error(error.response?.data?.detail || "Erè nan Punchout");
    } finally {
      setStartingPunchout(false);
    }
  };

  const formatCurrency = (amount, currency = 'HTG') => {
    return new Intl.NumberFormat('fr-HT').format(amount) + ' ' + currency;
  };

  if (loading) {
    return (
      <EnterpriseLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </EnterpriseLayout>
    );
  }

  return (
    <EnterpriseLayout>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">
              Bonjou, {user.name?.split(' ')[0] || 'Itilizatè'}! 👋
            </h1>
            <p className="text-gray-500 mt-1">
              {stats?.company?.name} • {user.role?.toUpperCase()}
            </p>
          </div>

          <Button
            onClick={startPunchoutSession}
            disabled={startingPunchout}
            className="bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6 px-8"
          >
            {startingPunchout ? (
              <>Ap ouvri...</>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5 mr-2" />
                Kòmanse Acha sou Amazon
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Kòmand</p>
                  <p className="text-3xl font-bold text-[#232F3E]">{stats?.orders?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">An Atant</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats?.orders?.pending || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Apwouve</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.orders?.approved || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Depans Total</p>
                  <p className="text-2xl font-bold text-[#FF9900]">
                    ${stats?.spending?.total_usd?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Budget Overview */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FF9900]" />
              Bidjè
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Bidjè Total</p>
                <p className="text-2xl font-bold text-[#232F3E]">
                  {formatCurrency(stats?.spending?.budget_total || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Itilize</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(stats?.spending?.total_htg || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Rete</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats?.spending?.budget_remaining || 0)}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF9900] to-orange-500 transition-all"
                  style={{
                    width: `${Math.min(
                      ((stats?.spending?.total_htg || 0) / (stats?.spending?.budget_total || 1)) * 100,
                      100
                    )}%`
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        {pendingOrders.length > 0 && (
          <Card className="border-0 shadow-lg border-l-4 border-l-yellow-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-700">
                <Clock className="w-5 h-5" />
                Kòmand Ap Tann Apwobasyon Ou ({pendingOrders.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingOrders.slice(0, 5).map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg"
                  >
                    <div>
                      <p className="font-semibold text-[#232F3E]">{order.order_number}</p>
                      <p className="text-sm text-gray-500">
                        {order.items?.length || 0} pwodwi • ${order.subtotal?.toLocaleString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/enterprise/orders/${order.id}`)}
                      className="bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    >
                      Revize
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {/* Giftcard Button - Super Admin and Giftcard Admin Only */}
          {['super_admin', 'giftcard_admin'].includes(user.role) && (
            <>
              <Card
                className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-purple-500 to-pink-500"
                onClick={() => navigate('/enterprise/giftcards')}
                data-testid="giftcard-btn"
              >
                <CardContent className="p-6 text-center">
                  <Gift className="w-10 h-10 mx-auto mb-3 text-white" />
                  <p className="font-semibold text-white">Kat Kado Amazon</p>
                </CardContent>
              </Card>

              <Card
                className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-green-600 to-emerald-700"
                onClick={() => navigate('/enterprise/giftcards?tab=meru')}
                data-testid="meru-admin-btn"
              >
                <CardContent className="p-6 text-center">
                  <Wallet className="w-10 h-10 mx-auto mb-3 text-white" />
                  <p className="font-semibold text-white">Rechaj Meru</p>
                </CardContent>
              </Card>

              <Card
                className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-slate-800 to-slate-950"
                onClick={() => navigate('/enterprise/giftcards?tab=crypto')}
                data-testid="crypto-admin-btn"
              >
                <CardContent className="p-6 text-center">
                  <div className="relative inline-block">
                    <Coins className="w-10 h-10 mx-auto mb-3 text-green-400" />
                    <Zap className="w-4 h-4 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <p className="font-semibold text-white">Gestion USDT</p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Marketplace Sellers - Super Admin Only */}
          {user.role === 'super_admin' && (
            <Card
              className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-[#232F3E] to-[#1a242f]"
              onClick={() => navigate('/enterprise/sellers')}
              data-testid="marketplace-sellers-btn"
            >
              <CardContent className="p-6 text-center">
                <Users className="w-10 h-10 mx-auto mb-3 text-[#FF9900]" />
                <p className="font-semibold text-white">Machann Marketplace</p>
              </CardContent>
            </Card>
          )}

          {/* LinkAmazon Buyers - Super Admin Only */}
          {user.role === 'super_admin' && (
            <Card
              className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-teal-500 to-emerald-600"
              onClick={() => navigate('/enterprise/linkamazon/buyers')}
              data-testid="linkamazon-buyers-btn"
            >
              <CardContent className="p-6 text-center">
                <Users className="w-10 h-10 mx-auto mb-3 text-white" />
                <p className="font-semibold text-white">Itilizatè Amazon</p>
              </CardContent>
            </Card>
          )}

          <Card
            className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow bg-gradient-to-br from-[#FF9900] to-orange-500"
            onClick={() => navigate('/enterprise/orders/new')}
          >
            <CardContent className="p-6 text-center">
              <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-black" />
              <p className="font-semibold text-black">Kreye Kòmand Tès</p>
            </CardContent>
          </Card>

          <Card
            className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/enterprise/orders')}
          >
            <CardContent className="p-6 text-center">
              <Package className="w-10 h-10 mx-auto mb-3 text-[#FF9900]" />
              <p className="font-semibold text-[#232F3E]">Wè Tout Kòmand</p>
            </CardContent>
          </Card>

          <Card
            className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/enterprise/users')}
          >
            <CardContent className="p-6 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-[#FF9900]" />
              <p className="font-semibold text-[#232F3E]">Jere Itilizatè</p>
            </CardContent>
          </Card>

          <Card
            className="border-0 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/enterprise/reports')}
          >
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 text-[#FF9900]" />
              <p className="font-semibold text-[#232F3E]">Rapò</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </EnterpriseLayout>
  );
};

